import type { AdminSchemas } from "@10twenty/api/admin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../api";
import { deletionConfirmed, KIND_LABELS, locationLabel, PLATFORM_LABELS, reportsLabel, when } from "../accounts";
import { useMe, useRole } from "../components/Shell";
import { roleLabel } from "../nav";
import { errorText, unwrap } from "../problem";

type User = AdminSchemas["UserDetail"];

// Opening an account is audited (`user.open`), so it is never refetched by
// itself; an action reloads it once.
export function UserPage() {
  const { userId = "" } = useParams();
  const user = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => unwrap(await api.GET("/users/{userId}", { params: { path: { userId } } })),
    // Every visit fetches (and is audited) afresh: nothing is kept once the page closes.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  if (user.isPending) return <p>Loading…</p>;
  if (user.isError) return <p className="error">{errorText(user.error)}</p>;
  return <UserDetail user={user.data} />;
}

function UserDetail({ user }: { user: User }) {
  const role = useRole();
  const me = useMe().data;
  const own = me?.user_id === user.id;
  return (
    <>
      <p>
        <Link to="/users">← Users</Link>
      </p>
      <h1>{user.name}</h1>
      {user.photo && <img className="profile-photo" src={user.photo} alt={`Profile photo of ${user.name}`} />}
      <table className="facts">
        <tbody>
          <Fact label="Email">{user.email}</Fact>
          <Fact label="Id">
            <code>{user.id}</code>
          </Fact>
          <Fact label="Created">{when(user.created_at)}</Fact>
          <Fact label="Last active">{when(user.last_active_at)}</Fact>
          <Fact label="Suspended">{user.suspended_at ? when(user.suspended_at) : "No"}</Fact>
          <Fact label="Staff role">{user.staff_role ? roleLabel(user.staff_role) : "—"}</Fact>
          <Fact label="Time zone">{user.time_zone ?? "—"}</Fact>
          <Fact label="Sharing paused">{user.sharing_paused ? "Yes, everywhere" : "No"}</Fact>
          <Fact label="Signed-in sessions">{user.sessions}</Fact>
          <Fact label="Supervision">
            {[user.supervised ? "Supervised" : null, user.guardian ? "Guardian" : null].filter(Boolean).join(" · ") ||
              "No"}
          </Fact>
          <Fact label="Reports against">{reportsLabel(user.reports.against)}</Fact>
          <Fact label="Reports by">{reportsLabel(user.reports.by)}</Fact>
        </tbody>
      </table>
      {role === "owner" && (
        <p>
          <Link to={`/audit?target_type=user&target_id=${user.id}`}>Audit log for this account</Link>
        </p>
      )}
      <Actions user={user} own={own} owner={role === "owner"} />
      <section className="stack">
        <h2>Circles ({user.circles.length})</h2>
        {user.circles.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Role</th>
                <th className="num">Members</th>
                <th>Joined</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {user.circles.map((circle) => (
                <tr key={circle.id}>
                  <td>
                    <Link to={`/circles/${circle.id}`}>{circle.name}</Link>
                  </td>
                  <td>{KIND_LABELS[circle.kind]}</td>
                  <td>{circle.role === "admin" ? "Admin" : "Member"}</td>
                  <td className="num">{circle.members}</td>
                  <td>{when(circle.joined_at)}</td>
                  <td>
                    {[circle.sharing_paused ? "Paused here" : null, circle.supervised ? "Supervised" : null]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="stack">
        <h2>Devices ({user.devices.length})</h2>
        {user.devices.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Device</th>
                <th>App</th>
                <th>OS</th>
                <th>Last seen</th>
                <th>Signed in</th>
                <th>Push</th>
                <th>Location setting</th>
              </tr>
            </thead>
            <tbody>
              {user.devices.map((device) => (
                <tr key={device.id}>
                  <td>
                    {device.name} <span className="muted">({PLATFORM_LABELS[device.platform]})</span>
                  </td>
                  <td>{device.app_version ?? "—"}</td>
                  <td>{device.os_version ?? "—"}</td>
                  <td>{when(device.last_seen_at)}</td>
                  <td>{device.signed_in ? "Yes" : "No"}</td>
                  <td>{device.push_registered ? "Registered" : "—"}</td>
                  <td>{locationLabel(device)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      <section className="stack">
        <h2>API tokens ({user.api_tokens.length})</h2>
        {user.api_tokens.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Circle</th>
                <th>Scope</th>
                <th>Created</th>
                <th>Last used</th>
              </tr>
            </thead>
            <tbody>
              {user.api_tokens.map((token) => (
                <tr key={token.id}>
                  <td>{token.name}</td>
                  <td>
                    <Link to={`/circles/${token.circle.id}`}>{token.circle.name}</Link>
                  </td>
                  <td>
                    <code>{token.scope}</code>
                  </td>
                  <td>{when(token.created_at)}</td>
                  <td>{when(token.last_used_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {user.reports.recent.length > 0 && (
        <section className="stack">
          <h2>Recent reports</h2>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Direction</th>
                <th>About</th>
                <th>Reason</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {user.reports.recent.map((report) => (
                <tr key={report.id}>
                  <td>{when(report.created_at)}</td>
                  <td>{report.direction === "against" ? "Against them" : "By them"}</td>
                  <td>{report.about_message ? "A message" : "The account"}</td>
                  <td>{report.reason.replaceAll("_", " ")}</td>
                  <td>
                    {report.status}
                    {report.resolution ? ` (${report.resolution.replaceAll("_", " ")})` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <tr>
      <th>{label}</th>
      <td>{children}</td>
    </tr>
  );
}

function Actions({ user, own, owner }: { user: User; own: boolean; owner: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [typed, setTyped] = useState("");
  const path = { params: { path: { userId: user.id } } };
  const done = (text: string) => {
    setNotice(text);
    void queryClient.invalidateQueries({ queryKey: ["user", user.id] });
  };
  const suspend = useMutation({
    mutationFn: async (on: boolean) =>
      unwrap(await api.POST(on ? "/users/{userId}/suspend" : "/users/{userId}/unsuspend", { ...path, body: {} })),
    onSuccess: (result) =>
      done(
        result.suspended
          ? `Suspended; ${result.sessions_revoked} ${result.sessions_revoked === 1 ? "session" : "sessions"} revoked.`
          : "No longer suspended. Revoked sessions and tokens stay revoked.",
      ),
  });
  const revoke = useMutation({
    mutationFn: async () => unwrap(await api.POST("/users/{userId}/revoke-sessions", { ...path, body: {} })),
    onSuccess: (result) =>
      done(`Signed out of ${result.sessions_revoked} ${result.sessions_revoked === 1 ? "session" : "sessions"}.`),
  });
  const remove = useMutation({
    mutationFn: async () => {
      const result = await api.POST("/users/{userId}/delete", { ...path, body: { email: typed.trim() } });
      if (result.error !== undefined || !result.response.ok) unwrap(result);
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["user", user.id] });
      queryClient.removeQueries({ queryKey: ["users"] });
      void navigate("/users", { replace: true });
    },
  });
  const removePhoto = useMutation({
    mutationFn: async () => unwrap(await api.POST("/users/{userId}/remove-avatar", { ...path, body: {} })),
    onSuccess: (result) =>
      done(result.removed ? "Photo removed for everyone. They may upload another." : "They had no photo."),
  });
  const busy = suspend.isPending || revoke.isPending || remove.isPending || removePhoto.isPending;
  const error = suspend.error ?? revoke.error ?? remove.error ?? removePhoto.error;

  function confirmSuspend(on: boolean) {
    const text = on
      ? `Suspend ${user.email}? They are signed out everywhere, their API tokens stop working and sign-in is refused.`
      : `Let ${user.email} sign in again?`;
    if (window.confirm(text)) suspend.mutate(on);
  }
  function confirmRevoke() {
    if (window.confirm(`Sign ${user.email} out of every device? Their API tokens keep working.`)) revoke.mutate();
  }
  function confirmRemovePhoto() {
    if (window.confirm(`Remove ${user.email}'s profile photo for everyone? They may upload another.`))
      removePhoto.mutate();
  }
  function submitDelete(event: FormEvent) {
    event.preventDefault();
    if (deletionConfirmed(typed, user.email)) remove.mutate();
  }

  return (
    <section className="stack">
      <h2>Actions</h2>
      <p className="muted">Every action is recorded in the audit log.</p>
      {notice && <p>{notice}</p>}
      {error && <p className="error">{errorText(error)}</p>}
      {own ? (
        <p className="muted">This is your own account; use the app to change it.</p>
      ) : (
        <div className="actions">
          {user.suspended_at ? (
            <button type="button" onClick={() => confirmSuspend(false)} disabled={busy}>
              Unsuspend
            </button>
          ) : (
            <button type="button" onClick={() => confirmSuspend(true)} disabled={busy}>
              Suspend
            </button>
          )}
          <button type="button" className="button-quiet" onClick={confirmRevoke} disabled={busy || user.sessions === 0}>
            Sign out everywhere
          </button>
          {user.photo && (
            <button type="button" className="button-quiet" onClick={confirmRemovePhoto} disabled={busy}>
              Remove photo
            </button>
          )}
          {owner && !deleting && (
            <button type="button" className="button-quiet" onClick={() => setDeleting(true)} disabled={busy}>
              Delete account…
            </button>
          )}
        </div>
      )}
      {owner && !own && deleting && (
        <form className="danger stack" onSubmit={submitDelete}>
          <p>
            Only for a verified deletion request. This deletes the account and everything it owns, as deleting it in the
            app does, and can't be undone. Type <strong>{user.email}</strong> to confirm.
          </p>
          <label>
            Email address
            <input
              value={typed}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
          <div className="actions">
            <button type="submit" disabled={busy || !deletionConfirmed(typed, user.email)}>
              {remove.isPending ? "Deleting…" : "Delete account"}
            </button>
            <button
              type="button"
              className="button-quiet"
              onClick={() => {
                setDeleting(false);
                setTyped("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
